import { Link, useRouteError } from 'react-router';
import { Button } from '~/components/ui/button';
import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const errorQuotes = [
  'Looks like our AI had an identity crisis. It tried to name itself and crashed.',
  "Our domain generator is currently in therapy after seeing one too many 'blockchain' requests.",
  'Error 404: Creativity not found. Have you tried turning your imagination off and on again?',
  'Our AI went to get coffee. Apparently, thinking of clever domain names is exhausting.',
  "This error is brought to you by the letter 'Oops' and the number '500'.",
  "Even our AI needs a break from generating variations of 'crypto' and 'web3' domains.",
  "Our algorithm just rage-quit after someone asked for 'metaverse' in their domain name for the 100th time.",
  "Domain suggestion engine is currently experiencing an existential crisis. It's asking, 'What's in a name?'",
  'Our AI went down a rabbit hole trying to understand why humans love puns in domain names so much.',
  "Error: Too many brilliant domain ideas. System couldn't handle the awesomeness.",
];

interface RouteError {
  message?: string;
  statusText?: string;
  status?: number;
}

export default function ErrorRoute() {
  const error = useRouteError() as RouteError;
  const [randomQuote, setRandomQuote] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    // Select a random quote on component mount
    const randomIndex = Math.floor(Math.random() * errorQuotes.length);
    setRandomQuote(errorQuotes[randomIndex]);
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4 text-center">
      <div className="mx-auto max-w-md space-y-6">
        <div className="flex justify-center">
          <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/20">
            <AlertTriangle className="h-10 w-10 text-red-600 dark:text-red-400" />
          </div>
        </div>

        <h1 className="text-3xl font-bold">
          {t('error.title', 'Oops! Something went wrong')}
        </h1>

        <div className="rounded-lg bg-slate-50/80 p-4 backdrop-blur-lg dark:bg-slate-800/50">
          <p className="text-lg italic">"{randomQuote}"</p>
        </div>

        <div className="text-sm text-slate-600 dark:text-slate-400">
          <p>
            {t('error.technicalDetails', 'Technical details: {{message}}', {
              message:
                error?.message ||
                error?.statusText ||
                t('error.unknownError', 'Unknown error'),
            })}
          </p>
        </div>

        <Button asChild className="w-full">
          <Link to="/">{t('error.backToHome', 'Take me back home')}</Link>
        </Button>
      </div>
    </div>
  );
}
